import { Alert, Box, Button, Divider, Grid, Stack } from '@mui/material'
import useCalculator from './hooks/useCalculator.ts'
import TextComponent from '@/core/components/form/TextComponent.tsx'
import { UseFormReturn } from 'react-hook-form'
import {
  calculatorFormSchema,
  Promotion
} from '@/modules/utilities/calculator/types/types.ts'
import { FC, useEffect } from 'react'
import SelectComponentControlled from '@/core/components/form/selectComponents/SelectComponentControlled.tsx'
import { TOptionLoader } from '@/core/types/OptionLoader.ts'
import { appConfig } from '@/config/app.ts'

interface Props {
  form: UseFormReturn<calculatorFormSchema>
  brands: TOptionLoader
  models: TOptionLoader
  articles: TOptionLoader
  categories: TOptionLoader
  rentability: TOptionLoader
  handleBuscar: (value: string) => void
  isLoadingDebt: boolean
  searchModels: (value: string) => void
  isLoadingProm: boolean
  promotionByCedule: (values: {
    cedula: string
    brandId: string
    modelId: string
  }) => void
  articlePromotion?: Promotion
  isLoadingCatRen: boolean
  rentabilityBycategory: (values: string) => void
  articleOthers: boolean
  articleApple: boolean
}

const CalculatorForm: FC<Props> = ({
  form,
  brands,
  models,
  articles,
  categories,
  rentability,
  handleBuscar,
  isLoadingDebt,
  searchModels,
  promotionByCedule,
  isLoadingProm,
  articlePromotion,
  isLoadingCatRen,
  articleOthers,
  articleApple,
  rentabilityBycategory
}) => {
  const {
    register,
    getValues,
    setValue,
    formState: { errors },
    control,
    reset,
    watch
  } = form

  const category = watch('category')
  const brand = watch('brand')
  const model = watch('model')

  const { closeModal } = useCalculator(reset)

  useEffect(() => {
    if (articleApple) {
      setValue('brand', appConfig.appleBrandId, { shouldValidate: true })
    } else if (getValues('brand') === appConfig.appleBrandId) {
      form.resetField('brand')
      form.resetField('model')
    }
  }, [articleApple, form, getValues, setValue])

  useEffect(() => {
    if (brand) {
      searchModels(brand)
      form.resetField('model')
    }
  }, [brand, form, searchModels])

  useEffect(() => {
    if (model) {
      const { cedula, brand } = getValues()
      if (cedula && brand)
        promotionByCedule({ cedula: cedula, brandId: brand, modelId: model })
    }
  }, [form, getValues, model, promotionByCedule])

  useEffect(() => {
    if (category) {
      rentabilityBycategory(category)
      form.resetField('brand')
      form.resetField('model')
      form.resetField('rentability')
    }
  }, [category, form, rentabilityBycategory, setValue])

  return (
    <form>
      <Box mx="auto" component="form">
        {/* Barra de búsqueda */}
        <Stack
          direction="row"
          alignItems="center"
          my={4}
          sx={{ justifyContent: 'center', width: '100%' }}
        >
          <TextComponent
            variant="outlined"
            placeholder="Buscar por cédula"
            sx={{
              outline: 'none',
              borderRadius: 0,
              borderTopLeftRadius: 4,
              borderBottomLeftRadius: 4
            }}
            helperText={errors.cedula?.message}
            error={!!errors.cedula?.message}
            {...register('cedula')}
          />
          <Button
            variant="contained"
            color="primary"
            sx={{
              borderRadius: 0,
              borderTopRightRadius: 4,
              borderBottomRightRadius: 4,
              height: 40
            }}
            loadingPosition="center"
            loading={isLoadingDebt}
            disabled={isLoadingDebt}
            onClick={() => handleBuscar(getValues().cedula)}
          >
            Buscar
          </Button>
        </Stack>
        {articlePromotion?.name && (
          <Grid size={{ xs: 12 }} sx={{ mb: 4 }}>
            <Alert severity="info">{articlePromotion?.name}</Alert>
          </Grid>
        )}
        <Grid container spacing={1}>
          <Grid size={{ xs: 6 }} sx={{ mb: 2 }}>
            <SelectComponentControlled
              loading={articles.isLoading}
              options={articles.options}
              helperText={errors.article?.message}
              error={!!errors.article?.message}
              label="Tipo de artículo *"
              aria-placeholder={'Buscar'}
              control={control}
              name="article"
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <SelectComponentControlled
              loading={categories.isLoading || isLoadingCatRen}
              options={categories.options}
              disabled={!articleOthers}
              label="Categoría *"
              name="category"
              control={control}
              helperText={errors.category?.message}
              error={!!errors.category?.message}
              fullWidth
            />
          </Grid>
        </Grid>
        <Divider sx={{ mb: 2, width: '100%' }} />
        <Grid container spacing={1}>
          <Grid size={{ xs: 6 }} sx={{ mb: 2 }}>
            <SelectComponentControlled
              loading={brands.isLoading}
              options={brands.options}
              disabled={articleOthers || articleApple}
              label="Marca *"
              fullWidth
              name="brand"
              control={control}
              helperText={errors.brand?.message}
              error={!!errors.brand?.message}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <SelectComponentControlled
              loading={models.isLoading || isLoadingProm}
              options={models.options}
              disabled={articleOthers}
              label="Modelo *"
              fullWidth
              name="model"
              control={control}
              helperText={errors.model?.message}
              error={!!errors.model?.message}
            />
          </Grid>
          <Grid size={{ xs: 6 }} sx={{ mb: 2 }}>
            <TextComponent
              label="Costo"
              fullWidth
              variant="outlined"
              placeholder="Costo"
              type="number"
              helperText={errors.cost?.message}
              error={!!errors.cost?.message}
              {...register('cost')}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <SelectComponentControlled
              loading={rentability.isLoading}
              options={rentability.options}
              label="Rentabilidad del Equipo *"
              fullWidth
              name="rentability"
              control={control}
              helperText={errors.rentability?.message}
              error={!!errors.rentability?.message}
            />
          </Grid>
          <Grid size={{ xs: 6 }} sx={{ mb: 2 }}>
            <TextComponent
              label="Abono Inicial"
              fullWidth
              type="number"
              variant="outlined"
              helperText={errors.payment_extra?.message}
              error={!!errors.payment_extra?.message}
              {...register('payment_extra')}
            />
          </Grid>
          <Stack
            direction="row"
            spacing={2}
            sx={{ justifyContent: 'end', width: '100%' }}
          >
            <Button
              sx={{
                minWidth: 110
              }}
              onClick={closeModal}
            >
              Cerrar
            </Button>
          </Stack>
        </Grid>
      </Box>
    </form>
  )
}

export default CalculatorForm
